/* ==========================================================================
   DONNA CUORE — theme.js
   Vanilla JS, no dependencies. Every interaction talks to Shopify's own
   storefront APIs (Ajax Cart, Section Rendering, Predictive Search), so
   cart, search, filters and checkout stay 100% native.
   ========================================================================== */
(() => {
  'use strict';

  const DC = window.DC || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, wait = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Money ---------- */
  function formatMoney(cents, format = DC.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (n, dec = 2, th = ',', dp = '.') => {
      const [whole, frac] = (n / 100).toFixed(dec).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (frac ? dp + frac : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt(value);
    const map = {
      amount: () => fmt(value, 2),
      amount_no_decimals: () => fmt(value, 0),
      amount_with_comma_separator: () => fmt(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(value, 0, '.', ','),
      amount_with_apostrophe_separator: () => fmt(value, 2, "'", '.'),
      amount_with_space_separator: () => fmt(value, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => fmt(value, 0, ' ', ',')
    };
    return format.replace(match[0], (map[match[1]] || map.amount)());
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  /* ==========================================================================
     Layers: drawers, modals, search overlay — one manager handles focus trap,
     escape key, scroll lock and focus return for all of them.
     ========================================================================== */
  const Layers = {
    stack: [],
    overlay: null,
    open(el, opener) {
      if (!el || this.stack.includes(el)) return;
      this.overlay = this.overlay || $('[data-overlay]');
      el.hidden = false;
      el._opener = opener || document.activeElement;
      // next frame so the transition runs
      requestAnimationFrame(() => {
        el.classList.add('is-open');
        el.setAttribute('aria-hidden', 'false');
      });
      if (!el.hasAttribute('data-no-overlay')) {
        this.overlay.hidden = false;
        requestAnimationFrame(() => this.overlay.classList.add('is-visible'));
      }
      document.body.classList.add('is-locked');
      this.stack.push(el);
      const focusTarget = $('[data-autofocus]', el) || $('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', el);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
      el.dispatchEvent(new CustomEvent('layer:open'));
      if (el.id === 'MobileMenu') $('[data-header]')?.classList.add('is-menu-open');
    },
    close(el = this.stack[this.stack.length - 1]) {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      this.stack = this.stack.filter((x) => x !== el);
      if (!this.stack.some((x) => !x.hasAttribute('data-no-overlay')) && this.overlay) {
        this.overlay.classList.remove('is-visible');
        setTimeout(() => { if (!this.stack.length) this.overlay.hidden = true; }, 320);
      }
      if (!this.stack.length) document.body.classList.remove('is-locked');
      if (el.id === 'MobileMenu') $('[data-header]')?.classList.remove('is-menu-open');
      $$('[aria-controls="' + el.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      el._opener && el._opener.focus && el._opener.focus({ preventScroll: true });
      el.dispatchEvent(new CustomEvent('layer:close'));
    },
    trap(e) {
      const el = this.stack[this.stack.length - 1];
      if (!el || e.key !== 'Tab') return;
      const f = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
        .filter((n) => n.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && Layers.stack.length) { e.preventDefault(); Layers.close(); }
    Layers.trap(e);
  });
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.open);
      if (target) {
        e.preventDefault();
        opener.setAttribute('aria-expanded', 'true');
        Layers.open(target, opener);
      }
      return;
    }
    if (e.target.closest('[data-close]')) { e.preventDefault(); Layers.close(e.target.closest('.is-open')); return; }
    if (e.target.matches('[data-overlay]')) Layers.close();
  });

  /* ==========================================================================
     Announcement bar rotation
     ========================================================================== */
  function initAnnouncement() {
    const bar = $('[data-announcement]');
    if (!bar) return;
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    let i = 0, timer;
    const show = (n) => {
      items[i].classList.remove('is-active'); items[i].setAttribute('aria-hidden', 'true');
      i = (n + items.length) % items.length;
      items[i].classList.add('is-active'); items[i].setAttribute('aria-hidden', 'false');
    };
    const start = () => { if (!reducedMotion) timer = setInterval(() => show(i + 1), Number(bar.dataset.speed || 5) * 1000); };
    const stop = () => clearInterval(timer);
    $('[data-announcement-prev]', bar)?.addEventListener('click', () => { stop(); show(i - 1); start(); });
    $('[data-announcement-next]', bar)?.addEventListener('click', () => { stop(); show(i + 1); start(); });
    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    start();
  }

  /* ==========================================================================
     Header: shrink on scroll, transparent over hero, mega menus
     ========================================================================== */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const threshold = 24;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();

    // Mega menu: hover intent on pointer devices, click/keyboard everywhere
    const items = $$('[data-mega-item]', header);
    let closeTimer;
    const closeAll = (except) => items.forEach((it) => {
      if (it === except) return;
      it.classList.remove('is-open');
      $('[data-mega-toggle]', it)?.setAttribute('aria-expanded', 'false');
    });
    const openItem = (it) => {
      closeAll(it);
      it.classList.add('is-open');
      $('[data-mega-toggle]', it)?.setAttribute('aria-expanded', 'true');
      header.classList.add('is-menu-open');
    };
    const closeItem = (it) => {
      it.classList.remove('is-open');
      $('[data-mega-toggle]', it)?.setAttribute('aria-expanded', 'false');
      if (!items.some((x) => x.classList.contains('is-open'))) header.classList.remove('is-menu-open');
    };
    items.forEach((it) => {
      const hasMega = !!$('.mega', it);
      if (!hasMega) return;
      it.addEventListener('mouseenter', () => { clearTimeout(closeTimer); openItem(it); });
      it.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => closeItem(it), 140); });
      const toggle = $('[data-mega-toggle]', it);
      toggle?.addEventListener('click', (e) => {
        e.preventDefault();
        it.classList.contains('is-open') ? closeItem(it) : openItem(it);
      });
      it.addEventListener('focusout', (e) => { if (!it.contains(e.relatedTarget)) closeItem(it); });
      it.addEventListener('keydown', (e) => { if (e.key === 'Escape' && it.classList.contains('is-open')) { e.stopPropagation(); closeItem(it); toggle?.focus(); } });
    });

    // Mobile menu accordions
    $$('[data-accordion-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (panel) panel.hidden = open;
      });
    });
  }

  /* ==========================================================================
     Cart — Shopify Ajax Cart API + Section Rendering API
     ========================================================================== */
  const Cart = {
    sectionsToRender() {
      const ids = ['cart-drawer'];
      if ($('[data-cart-page]')) ids.push($('[data-cart-page]').dataset.sectionId);
      return ids;
    },
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionsToRender(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || DC.strings.cartError);
      return data;
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        $$('[data-cart-replace]', doc).forEach((fresh) => {
          const key = fresh.dataset.cartReplace;
          const current = $(`[data-cart-replace="${key}"]`);
          if (current) current.replaceWith(fresh);
        });
        const countEl = $('[data-cart-count-source]', doc);
        if (countEl) this.setCount(Number(countEl.dataset.cartCountSource));
      });
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = n > 0 ? n : ''; el.dataset.count = n; });
      $$('[data-cart-count-label]').forEach((el) => { el.textContent = n; });
    },
    async add(formData, button) {
      button?.classList.add('is-loading');
      button?.setAttribute('aria-busy', 'true');
      try {
        const body = { items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }] };
        // preserve line-item properties and selling plans if present
        const props = {};
        for (const [k, v] of formData.entries()) {
          const m = k.match(/^properties\[(.+)\]$/);
          if (m && v) props[m[1]] = v;
        }
        if (Object.keys(props).length) body.items[0].properties = props;
        if (formData.get('selling_plan')) body.items[0].selling_plan = formData.get('selling_plan');
        const data = await this.request(DC.routes.cartAdd + '.js', body);
        this.render(data.sections);
        if (Layers.stack.length) Layers.close(Layers.stack.find((l) => l.id === 'QuickView'));
        const drawer = $('#CartDrawer');
        if (drawer && !$('[data-cart-page]')) Layers.open(drawer, button);
        else toast(DC.strings.added);
      } catch (err) {
        toast(err.message);
      } finally {
        button?.classList.remove('is-loading');
        button?.removeAttribute('aria-busy');
      }
    },
    async change(line, quantity, row) {
      row?.classList.add('is-updating');
      try {
        const data = await this.request(DC.routes.cartChange + '.js', { line: Number(line), quantity });
        this.render(data.sections);
        if (!data.sections) window.location.reload();
      } catch (err) {
        toast(err.message);
        row?.classList.remove('is-updating');
      }
    }
  };

  function initCart() {
    // Any product form (product page, quick view, card quick-add)
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const btn = e.submitter && e.submitter.name !== 'checkout' ? e.submitter : $('[type="submit"]', form);
      if (btn?.disabled) return;
      Cart.add(new FormData(form), btn);
    });

    // Quantity + remove inside cart drawer/page (event delegation survives re-render)
    document.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-cart-remove]');
      if (rm) { e.preventDefault(); Cart.change(rm.dataset.line, 0, rm.closest('.cart-item')); return; }
      const step = e.target.closest('[data-cart-qty-step]');
      if (step) {
        const input = $('input', step.closest('.qty'));
        const next = Math.max(0, Number(input.value) + Number(step.dataset.cartQtyStep));
        input.value = next;
        Cart.change(input.dataset.line, next, step.closest('.cart-item'));
      }
    });
    document.addEventListener('change', debounce((e) => {
      const input = e.target.closest('input[data-cart-qty]');
      if (input) Cart.change(input.dataset.line, Math.max(0, Number(input.value)), input.closest('.cart-item'));
    }, 300));

    // Cart note
    document.addEventListener('input', debounce((e) => {
      if (!e.target.matches('[data-cart-note]')) return;
      fetch(DC.routes.cartUpdate + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: e.target.value })
      });
    }, 500));

    // Keep drawer fresh after bfcache restore (e.g. back from checkout)
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      fetch(`${window.location.pathname}?sections=cart-drawer`).then((r) => r.json()).then((s) => Cart.render(s)).catch(() => {});
    });
  }

  /* Generic quantity stepper for product forms */
  function initQtySteppers(ctx = document) {
    $$('[data-qty]', ctx).forEach((wrap) => {
      if (wrap._init) return; wrap._init = true;
      const input = $('input', wrap);
      const min = Number(input.min || 1);
      const sync = () => { $('[data-qty-minus]', wrap).disabled = Number(input.value) <= min; };
      $('[data-qty-minus]', wrap).addEventListener('click', () => { input.value = Math.max(min, Number(input.value) - 1); sync(); input.dispatchEvent(new Event('change', { bubbles: true })); });
      $('[data-qty-plus]', wrap).addEventListener('click', () => { input.value = Number(input.value) + 1; sync(); input.dispatchEvent(new Event('change', { bubbles: true })); });
      input.addEventListener('change', sync);
      sync();
    });
  }

  /* ==========================================================================
     Product: variant picker, gallery, lightbox, sticky add-to-cart
     ========================================================================== */
  function initProduct(root) {
    if (!root || root._init) return;
    root._init = true;
    const jsonEl = $('[data-product-json]', root);
    const product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    const form = $('form[data-product-form]', root);
    const idInput = form ? $('input[name="id"]', form) : null;
    const updateUrl = root.dataset.updateUrl === 'true';

    initQtySteppers(root);
    initGallery(root);

    if (!product || !form || product.variants.length < 2) return;

    const pickers = $$('[data-option-index]', root);
    const currentOptions = () => pickers.map((fs) => {
      const checked = $('input:checked', fs);
      const sel = $('select', fs);
      return checked ? checked.value : sel ? sel.value : null;
    });

    const markAvailability = (opts) => {
      // Grey-out values that can't combine with the other selected options
      pickers.forEach((fs, idx) => {
        $$('input[type="radio"]', fs).forEach((input) => {
          const test = [...opts]; test[idx] = input.value;
          const ok = product.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    };

    const render = (variant) => {
      const atcs = $$('[data-atc]', document).filter((b) => root.contains(b) || b.closest('[data-sticky-atc]'));
      const labelFor = (v) => (!v ? DC.strings.unavailable : v.available ? DC.strings.addToCart : DC.strings.soldOut);
      atcs.forEach((btn) => {
        btn.disabled = !variant || !variant.available;
        const label = $('[data-atc-label]', btn) || btn;
        label.textContent = labelFor(variant);
      });
      const payBtn = $('[data-payment-wrap]', root);
      if (payBtn) payBtn.hidden = !variant || !variant.available;
      if (!variant) return;
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      $$('[data-price]', document).filter((p) => root.contains(p) || p.closest('[data-sticky-atc]')).forEach((p) => {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        p.classList.toggle('price--sale', !!onSale);
        $('[data-price-current]', p).textContent = formatMoney(variant.price);
        const cmp = $('[data-price-compare]', p);
        if (cmp) { cmp.textContent = onSale ? formatMoney(variant.compare_at_price) : ''; cmp.hidden = !onSale; }
      });
      $$('[data-sale-badge]', root).forEach((b) => { b.hidden = !(variant.compare_at_price > variant.price); });
      const sku = $('[data-sku]', root);
      if (sku) { sku.textContent = variant.sku || ''; sku.parentElement.hidden = !variant.sku; }
      pickers.forEach((fs, i) => { const out = $('output', fs); if (out) out.textContent = variant.options[i]; });
      if (variant.featured_media) root.dispatchEvent(new CustomEvent('gallery:goto', { detail: { mediaId: variant.featured_media.id } }));
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    const onChange = () => {
      const opts = currentOptions();
      const variant = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      markAvailability(opts);
      render(variant);
    };
    pickers.forEach((fs) => fs.addEventListener('change', onChange));
    markAvailability(currentOptions());
  }

  function initGallery(root) {
    const gallery = $('[data-gallery]', root);
    if (!gallery) return;
    const track = $('[data-gallery-track]', gallery);
    if (!track) return;
    const slides = $$('[data-gallery-slide]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const dots = $$('[data-gallery-dots] span', gallery);
    const counter = $('[data-gallery-counter]', gallery);
    let index = 0;

    const goTo = (i, smooth = true) => {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
    };
    const setActive = (i) => {
      index = i;
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      if (counter) counter.textContent = `${i + 1} / ${slides.length}`;
      // pause videos that scrolled away
      slides.forEach((s, n) => { if (n !== i) $$('video', s).forEach((v) => v.pause()); });
    };
    track.addEventListener('scroll', debounce(() => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      if (i !== index) setActive(i);
    }, 60), { passive: true });
    thumbs.forEach((t, i) => t.addEventListener('click', () => { goTo(i); setActive(i); }));
    $('[data-gallery-prev]', gallery)?.addEventListener('click', () => goTo(index - 1));
    $('[data-gallery-next]', gallery)?.addEventListener('click', () => goTo(index + 1));
    root.addEventListener('gallery:goto', (e) => {
      const i = slides.findIndex((s) => Number(s.dataset.mediaId) === e.detail.mediaId);
      if (i > -1) { goTo(i); setActive(i); }
    });
    setActive(0);

    // Lightbox (fullscreen + tap/click zoom)
    const lightbox = $('[data-lightbox]', root);
    if (!lightbox) return;
    const lbTrack = $('[data-lightbox-track]', lightbox);
    const lbSlides = $$('[data-lightbox-slide]', lightbox);
    const openLb = (i) => {
      lightbox.classList.add('is-open');
      document.body.classList.add('is-locked');
      lbTrack.scrollLeft = lbSlides[i]?.offsetLeft || 0;
      $('[data-lightbox-close]', lightbox).focus();
    };
    const closeLb = () => {
      lightbox.classList.remove('is-open');
      if (!Layers.stack.length) document.body.classList.remove('is-locked');
      lbSlides.forEach((s) => s.classList.remove('is-zoomed'));
      slides[index]?.focus?.();
    };
    slides.forEach((s, i) => {
      if (s.dataset.mediaType !== 'image') return;
      s.addEventListener('click', () => openLb(i));
      s.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLb(i); } });
    });
    $('[data-gallery-zoom]', gallery)?.addEventListener('click', () => openLb(index));
    $('[data-lightbox-close]', lightbox).addEventListener('click', closeLb);
    lightbox.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeLb(); }
      if (e.key === 'ArrowRight') lbTrack.scrollBy({ left: lbTrack.clientWidth, behavior: 'smooth' });
      if (e.key === 'ArrowLeft') lbTrack.scrollBy({ left: -lbTrack.clientWidth, behavior: 'smooth' });
    });
    $('[data-lightbox-prev]', lightbox)?.addEventListener('click', () => lbTrack.scrollBy({ left: -lbTrack.clientWidth, behavior: 'smooth' }));
    $('[data-lightbox-next]', lightbox)?.addEventListener('click', () => lbTrack.scrollBy({ left: lbTrack.clientWidth, behavior: 'smooth' }));
    lbSlides.forEach((s) => {
      s.addEventListener('click', (e) => {
        const img = $('img', s);
        if (!s.classList.contains('is-zoomed')) {
          const r = img.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        }
        s.classList.toggle('is-zoomed');
      });
      s.addEventListener('mousemove', (e) => {
        if (!s.classList.contains('is-zoomed')) return;
        const img = $('img', s); const r = s.getBoundingClientRect();
        img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      });
    });
  }

  function initStickyATC() {
    const bar = $('[data-sticky-atc]');
    const main = $('[data-main-atc]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    const footer = $('.footer');
    let mainVisible = true, footerVisible = false;
    const sync = () => bar.classList.toggle('is-visible', !mainVisible && !footerVisible);
    new IntersectionObserver(([e]) => { mainVisible = e.isIntersecting || e.boundingClientRect.top > 0; sync(); }).observe(main);
    if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; sync(); }).observe(footer);
    // sticky button submits the real product form
    $('[data-sticky-submit]', bar)?.addEventListener('click', () => {
      const form = $('form[data-product-form]', $('[data-product-root]'));
      form?.requestSubmit($('[data-main-atc]'));
    });
  }

  /* ==========================================================================
     Quick view — renders sections/quick-view.liquid for the real product
     ========================================================================== */
  function initQuickView() {
    const modal = $('#QuickView');
    if (!modal) return;
    const body = $('[data-quick-view-body]', modal);
    const loading = body.innerHTML;
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-view]');
      if (!btn) return;
      e.preventDefault();
      body.innerHTML = loading;
      Layers.open(modal, btn);
      try {
        const url = btn.dataset.quickView.split('?')[0];
        const res = await fetch(`${url}?section_id=quick-view`);
        if (!res.ok) throw new Error();
        const doc = parseHTML(await res.text());
        const content = $('[data-product-root]', doc);
        body.innerHTML = '';
        body.appendChild(content);
        initProduct(content);
        Wishlist.sync(content);
        $('[data-autofocus]', content)?.focus();
      } catch (_) {
        window.location.href = btn.dataset.quickView;
      }
    });
  }

  /* ==========================================================================
     Wishlist — stored in the shopper's browser (no third-party app)
     ========================================================================== */
  const Wishlist = {
    key: 'dc:wishlist',
    read() { try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (_) { return []; } },
    write(list) { try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (_) {} this.sync(); },
    has(handle) { return this.read().includes(handle); },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      if (i > -1) { list.splice(i, 1); toast(DC.strings.wishlistRemove); }
      else { list.unshift(handle); toast(DC.strings.wishlistAdd); }
      this.write(list);
    },
    sync(ctx = document) {
      const list = this.read();
      $$('[data-wishlist-toggle]', ctx).forEach((b) => {
        const on = list.includes(b.dataset.wishlistToggle);
        b.setAttribute('aria-pressed', String(on));
        const label = b.dataset[on ? 'labelRemove' : 'labelAdd'];
        if (label) b.setAttribute('aria-label', label);
      });
      $$('[data-wishlist-count]').forEach((el) => { el.textContent = list.length || ''; el.dataset.count = list.length; });
    },
    async renderPage() {
      const grid = $('[data-wishlist-grid]');
      if (!grid) return;
      const list = this.read();
      const empty = $('[data-wishlist-empty]');
      const count = $('[data-wishlist-page-count]');
      if (count) count.textContent = list.length;
      if (!list.length) { grid.innerHTML = ''; empty.hidden = false; return; }
      empty.hidden = true;
      const cards = await Promise.all(list.map(async (handle) => {
        try {
          const res = await fetch(`${DC.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?view=card`);
          if (!res.ok) { return { handle, gone: true }; }
          return { handle, html: await res.text() };
        } catch (_) { return { handle }; }
      }));
      // drop products that no longer exist
      const gone = cards.filter((c) => c.gone).map((c) => c.handle);
      if (gone.length) { try { localStorage.setItem(this.key, JSON.stringify(list.filter((h) => !gone.includes(h)))); } catch (_) {} }
      grid.innerHTML = cards.filter((c) => c.html).map((c) => c.html).join('');
      this.sync();
      initReveal(grid);
      if (!grid.children.length) empty.hidden = false;
    }
  };

  function initWishlist() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      Wishlist.toggle(btn.dataset.wishlistToggle);
      if ($('[data-wishlist-grid]') && !Wishlist.has(btn.dataset.wishlistToggle)) {
        btn.closest('.card')?.remove();
        const count = $('[data-wishlist-page-count]');
        if (count) count.textContent = Wishlist.read().length;
        if (!Wishlist.read().length) $('[data-wishlist-empty]').hidden = false;
      }
    });
    window.addEventListener('storage', (e) => { if (e.key === Wishlist.key) Wishlist.sync(); });
    Wishlist.sync();
    Wishlist.renderPage();
  }

  /* ==========================================================================
     Predictive search overlay
     ========================================================================== */
  function initSearch() {
    const overlay = $('#SearchOverlay');
    if (!overlay) return;
    const input = $('[data-search-input]', overlay);
    const results = $('[data-search-results]', overlay);
    const idle = $('[data-search-idle]', overlay);
    const recentWrap = $('[data-recent-searches]', overlay);
    const key = 'dc:recent-searches';
    let controller;

    const readRecent = () => { try { return JSON.parse(localStorage.getItem(key)) || []; } catch (_) { return []; } };
    const saveRecent = (q) => {
      q = q.trim(); if (!q) return;
      const list = [q, ...readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 6);
      try { localStorage.setItem(key, JSON.stringify(list)); } catch (_) {}
    };
    const renderRecent = () => {
      if (!recentWrap) return;
      const list = readRecent();
      recentWrap.hidden = !list.length;
      const ul = $('ul', recentWrap);
      ul.innerHTML = list.map((q) => `<li><a href="${DC.routes.search}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">${q.replace(/[<>&"]/g, '')}</a></li>`).join('');
    };
    $('[data-recent-clear]', overlay)?.addEventListener('click', () => { try { localStorage.removeItem(key); } catch (_) {} renderRecent(); });

    const search = debounce(async (q) => {
      if (!q) { results.hidden = true; idle.hidden = false; return; }
      controller?.abort();
      controller = new AbortController();
      try {
        const params = new URLSearchParams({
          q, section_id: 'predictive-search',
          'resources[type]': 'product,collection,query',
          'resources[limit]': '8',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
        });
        const res = await fetch(`${DC.routes.predictiveSearch}?${params}`, { signal: controller.signal });
        const doc = parseHTML(await res.text());
        const fresh = $('[data-predictive-results]', doc);
        results.innerHTML = fresh ? fresh.innerHTML : '';
        results.hidden = false; idle.hidden = true;
      } catch (err) { if (err.name !== 'AbortError') results.hidden = true; }
    }, 220);

    input.addEventListener('input', () => search(input.value.trim()));
    $('form', overlay).addEventListener('submit', () => saveRecent(input.value));
    results.addEventListener('click', (e) => { if (e.target.closest('a')) saveRecent(input.value); });
    overlay.addEventListener('layer:open', () => { renderRecent(); setTimeout(() => input.focus(), 50); });

    // "/" opens search on desktop
    document.addEventListener('keydown', (e) => {
      if (e.key === '/' && !e.target.closest('input, textarea, select, [contenteditable]')) {
        e.preventDefault(); Layers.open(overlay);
      }
    });
  }

  /* ==========================================================================
     Collection filtering & sorting — Shopify Search & Discovery facets
     ========================================================================== */
  function initFacets() {
    const container = $('[data-collection-container]');
    if (!container) return;
    const sectionId = container.dataset.sectionId;

    const fetchAndRender = async (search, push = true) => {
      const url = `${window.location.pathname}?${search}`;
      $('[data-collection-body]')?.classList.add('is-loading');
      try {
        const res = await fetch(`${window.location.pathname}?${search}${search ? '&' : ''}section_id=${sectionId}`);
        const doc = parseHTML(await res.text());
        ['[data-collection-body]', '[data-facets-drawer-body]', '[data-active-facets]', '[data-product-count]', '[data-facets-sidebar]'].forEach((sel) => {
          const fresh = $(sel, doc); const current = $(sel);
          if (fresh && current) current.replaceWith(fresh);
        });
        if (push) window.history.pushState({ search }, '', url);
        Wishlist.sync(); initReveal(); bindForms();
      } catch (_) { window.location.href = url; }
    };

    const serialize = (form) => {
      const fd = new FormData(form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) { if (v !== '') params.append(k, v); }
      const sort = $('[data-sort-select]');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      return params.toString();
    };

    const onChange = debounce((form) => fetchAndRender(serialize(form)), 350);
    function bindForms() {
      $$('form[data-facets-form]').forEach((form) => {
        if (form._bound) return; form._bound = true;
        form.addEventListener('change', () => onChange(form));
        form.addEventListener('input', (e) => { if (e.target.type === 'number') onChange(form); });
        form.addEventListener('submit', (e) => { e.preventDefault(); fetchAndRender(serialize(form)); Layers.close($('#FacetsDrawer')); });
      });
    }
    bindForms();

    document.addEventListener('change', (e) => {
      if (!e.target.matches('[data-sort-select]')) return;
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', e.target.value); params.delete('page');
      fetchAndRender(params.toString());
    });
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-facet-remove]');
      if (!link) return;
      e.preventDefault();
      fetchAndRender(new URL(link.href, window.location.origin).searchParams.toString());
    });
    window.addEventListener('popstate', (e) => fetchAndRender(e.state?.search ?? window.location.search.slice(1), false));
  }

  /* ==========================================================================
     Rails (horizontal carousels)
     ========================================================================== */
  function initRails() {
    $$('[data-rail]').forEach((rail) => {
      const track = $('[data-rail-track]', rail);
      if (!track) return;
      const prev = $('[data-rail-prev]', rail);
      const next = $('[data-rail-next]', rail);
      const bar = $('[data-rail-progress]', rail);
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;
        if (bar) {
          const w = Math.min(100, (track.clientWidth / track.scrollWidth) * 100);
          bar.style.setProperty('--p-w', `${w}%`);
          bar.style.setProperty('--p-x', `${max > 0 ? (track.scrollLeft / max) * ((100 - w) / w) * 100 : 0}%`);
          bar.parentElement.hidden = max <= 4;
        }
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ==========================================================================
     Motion: scroll reveal + restrained parallax
     ========================================================================== */
  let revealObserver;
  function initReveal(ctx = document) {
    const els = $$('[data-reveal]:not(.is-revealed)', ctx);
    if (reducedMotion || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-revealed')); return; }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); revealObserver.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => revealObserver.observe(el));
  }

  function initParallax() {
    if (reducedMotion) return;
    const els = $$('[data-parallax]');
    if (!els.length) return;
    let ticking = false;
    const run = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const speed = Number(el.dataset.parallax || 0.08);
        const offset = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(run); ticking = true; } }, { passive: true });
    run();
  }


  /* ==========================================================================
     Product recommendations (Shopify Recommendations API), loaded near viewport
     ========================================================================== */
  function initRecommendations() {
    $$('[data-recommendations]').forEach((el) => {
      const load = async () => {
        try {
          const res = await fetch(el.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = $('[data-recommendations]', doc);
          if (!fresh || !fresh.innerHTML.trim()) return;
          el.innerHTML = fresh.innerHTML;
          el.hidden = false;
          initRails(); initReveal(el); Wishlist.sync(el);
        } catch (_) { /* leave hidden */ }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' });
      // the section is hidden (0px), so observe its previous sibling area instead
      io.observe(el.parentElement);
    });
  }

  /* ==========================================================================
     Misc: localization auto-submit, hero video, forms
     ========================================================================== */
  function initMisc() {
    // Country / language selectors submit Shopify's localization form
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-localization-select]')) e.target.form.submit();
    });
    // Pause hero video when reduced motion
    if (reducedMotion) $$('video[autoplay]').forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });
    // Scroll newsletter forms into view after a post (Shopify reloads the page)
    const posted = $('[data-form-posted]');
    if (posted) posted.scrollIntoView({ block: 'center' });
  }

  /* ---------- Boot ---------- */
  function boot() {
    initAnnouncement();
    initHeader();
    initCart();
    $$('[data-product-root]').forEach(initProduct);
    initStickyATC();
    initQuickView();
    initWishlist();
    initSearch();
    initFacets();
    initRails();
    initRecommendations();
    initReveal();
    initParallax();
    initMisc();
    initQtySteppers();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-init sections when merchants edit them
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-product-root]', e.target).forEach(initProduct);
    initRails(); initReveal(e.target); initAnnouncement(); Wishlist.sync();
  });

  window.DC.formatMoney = formatMoney;
})();
